/**
 * The dialog that opens when an item needs a choice before it can go on the
 * bill: a size, a crust, extras.
 *
 * Only opens when it has something to ask. An item with no variants and no
 * modifier groups goes straight onto the order from the tap - putting a dialog
 * in front of every item would double the taps for a whole menu to serve the
 * handful that need one.
 *
 * The running total updates as choices are made, so the cashier can read the
 * price to the customer before committing.
 */

import { useState, useEffect, useMemo } from 'react';
import { FiPlus, FiMinus } from 'react-icons/fi';

import Modal from '../ui/Modal';
import { money } from '../../utils/format';

/** Does this item need to ask anything at all? */
export const itemNeedsDialog = (item) =>
    (item?.variants?.length > 0) || (item?.modifierGroups?.length > 0);

const ModifierModal = ({ open, item, initialVariantId = null, onClose, onConfirm }) => {
    const [variantId, setVariantId] = useState(null);
    const [selected, setSelected] = useState({});   // groupId -> [modifierId]
    const [quantity, setQuantity] = useState(1);
    const [note, setNote] = useState('');

    // Reset when a different item is opened, and pre-select the defaults so a
    // cashier who agrees with them can confirm in one tap. A size scanned by its
    // own barcode is already decided, so it beats the default.
    useEffect(() => {
        if (!open || !item) return;

        const defaultVariant = item.variants?.find((v) => v.variantId === initialVariantId)
            || item.variants?.find((v) => v.isDefault) || item.variants?.[0];
        setVariantId(defaultVariant?.variantId ?? null);
        setQuantity(1);
        setNote('');

        const preselected = {};
        for (const group of item.modifierGroups || []) {
            const defaults = group.modifiers.filter((m) => m.isDefaultSelected).map((m) => m.modifierId);
            if (defaults.length) preselected[group.modifierGroupId] = defaults;
        }
        setSelected(preselected);
    }, [open, item]);

    const variant = item?.variants?.find((v) => v.variantId === variantId) || null;
    const unitRate = variant ? variant.price : (item?.price ?? 0);

    /**
     * What a modifier costs on the size currently chosen.
     *
     * Extra cheese is Rs. 120 on a Small pizza and Rs. 300 on an XLarge, so the
     * price shown has to follow the size selector. A modifier with no per-size
     * override keeps its base price.
     */
    const priceOf = (modifier) => {
        if (variantId && modifier.priceByVariant) {
            const override = modifier.priceByVariant[variantId];
            if (override !== undefined) return override;
        }
        return modifier.price;
    };

    const chosenModifiers = useMemo(() => {
        if (!item) return [];
        const all = [];
        for (const group of item.modifierGroups || []) {
            const ids = selected[group.modifierGroupId] || [];
            for (const id of ids) {
                const modifier = group.modifiers.find((m) => m.modifierId === id);
                if (!modifier) continue;

                const price = variantId && modifier.priceByVariant?.[variantId] !== undefined
                    ? modifier.priceByVariant[variantId]
                    : modifier.price;

                all.push({ ...modifier, price, groupName: group.name });
            }
        }
        return all;
    }, [item, selected, variantId]);

    const modifierTotal = chosenModifiers.reduce((total, m) => total + m.price, 0);
    const lineTotal = (unitRate + modifierTotal) * quantity;

    /** Required groups must be answered before the item can be added. */
    const unmetGroups = (item?.modifierGroups || []).filter((group) => {
        const count = (selected[group.modifierGroupId] || []).length;
        return group.isRequired && count < Math.max(1, group.minSelection);
    });

    const toggle = (group, modifierId) => {
        setSelected((current) => {
            const chosen = current[group.modifierGroupId] || [];
            const isChosen = chosen.includes(modifierId);

            // A single-choice group replaces rather than accumulates - tapping
            // "Large" after "Medium" should not leave both selected.
            if (group.maxSelection === 1) {
                return { ...current, [group.modifierGroupId]: isChosen ? [] : [modifierId] };
            }

            if (isChosen) {
                return { ...current, [group.modifierGroupId]: chosen.filter((id) => id !== modifierId) };
            }

            if (chosen.length >= group.maxSelection) return current;
            return { ...current, [group.modifierGroupId]: [...chosen, modifierId] };
        });
    };

    if (!item) return null;

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={item.name}
            subtitle={item.categoryName}
            width="md"
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className="btn-primary min-w-[11rem]"
                        disabled={unmetGroups.length > 0}
                        onClick={() => onConfirm({
                            item,
                            variantId,
                            variantName: variant?.variantName ?? variant?.name ?? null,
                            unitRate,
                            quantity,
                            itemNote: note.trim() || null,
                            modifiers: chosenModifiers.map((m) => ({
                                modifierId: m.modifierId,
                                name: m.name,
                                price: m.price,
                                quantity: 1
                            }))
                        })}
                    >
                        {unmetGroups.length > 0
                            ? `Choose ${unmetGroups[0].name}`
                            : <>Add · Rs. {money(lineTotal)}</>}
                    </button>
                </>
            )}
        >
            <div className="space-y-5">
                {/* Sizes */}
                {item.variants?.length > 0 && (
                    <section>
                        <h3 className="mb-2 text-sm font-semibold text-slate-700">Size</h3>
                        <div className="grid grid-cols-3 gap-2">
                            {item.variants.map((v) => (
                                <button
                                    key={v.variantId}
                                    type="button"
                                    onClick={() => setVariantId(v.variantId)}
                                    className={`rounded-lg border px-3 py-3 text-center transition-colors
                                                ${variantId === v.variantId
                                                    ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-500'
                                                    : 'border-slate-300 bg-white hover:border-brand-400'}`}
                                >
                                    <p className="text-sm font-medium text-slate-800">{v.name}</p>
                                    <p className="tabular text-sm text-brand-800">Rs. {money(v.price)}</p>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* Modifier groups */}
                {item.modifierGroups?.map((group) => {
                    const chosen = selected[group.modifierGroupId] || [];
                    const isUnmet = unmetGroups.some((g) => g.modifierGroupId === group.modifierGroupId);

                    return (
                        <section key={group.modifierGroupId}>
                            <div className="mb-2 flex items-baseline justify-between gap-2">
                                <h3 className="text-sm font-semibold text-slate-700">
                                    {group.name}
                                    {group.isRequired && <span className="ml-1 text-red-500">*</span>}
                                </h3>
                                <span className={`text-xs ${isUnmet ? 'font-medium text-red-600' : 'text-slate-500'}`}>
                                    {group.maxSelection === 1
                                        ? 'Choose one'
                                        : `Choose up to ${group.maxSelection}`}
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {group.modifiers.map((modifier) => {
                                    const isChosen = chosen.includes(modifier.modifierId);
                                    const isFull = !isChosen
                                        && group.maxSelection > 1
                                        && chosen.length >= group.maxSelection;

                                    return (
                                        <button
                                            key={modifier.modifierId}
                                            type="button"
                                            disabled={isFull}
                                            onClick={() => toggle(group, modifier.modifierId)}
                                            className={`rounded-lg border px-3 py-2.5 text-left transition-colors
                                                        ${isChosen
                                                            ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-500'
                                                            : 'border-slate-300 bg-white hover:border-brand-400'}
                                                        ${isFull ? 'cursor-not-allowed opacity-40' : ''}`}
                                        >
                                            <p className="truncate text-sm font-medium text-slate-800">
                                                {modifier.name}
                                            </p>
                                            {priceOf(modifier) > 0 && (
                                                <p className="tabular text-xs text-brand-700">
                                                    + Rs. {money(priceOf(modifier))}
                                                </p>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}

                {/* Quantity and note */}
                <section className="flex items-end gap-4 border-t border-slate-200 pt-4">
                    <div>
                        <label className="label">Quantity</label>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                                className="btn-secondary h-11 w-11 !px-0"
                            >
                                <FiMinus />
                            </button>
                            <span className="tabular w-12 text-center text-lg font-semibold">{quantity}</span>
                            <button
                                type="button"
                                onClick={() => setQuantity((q) => q + 1)}
                                className="btn-secondary h-11 w-11 !px-0"
                            >
                                <FiPlus />
                            </button>
                        </div>
                    </div>

                    <div className="min-w-0 flex-1">
                        <label className="label">Note for the kitchen</label>
                        <input
                            className="input"
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            placeholder="no onions, extra spicy…"
                            maxLength={200}
                        />
                    </div>
                </section>
            </div>
        </Modal>
    );
};

export default ModifierModal;
